import { useCallback, useRef, useState } from 'react';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api').replace(/\/$/, '');
const TRANSLATE_URL = `${API_BASE}/translate`;

const translationMemoryCache = {};
const inFlightTranslations = {};

function makeCacheKey(lang, question) {
  const qid = String(question?.id || 'no-id');
  const stem = String(question?.scenario || question?.question || '').slice(0, 60);
  return `${lang}:${qid}:${stem}`;
}

function normalizeOptions(options) {
  if (Array.isArray(options)) {
    return options.map((opt, idx) => ({ key: opt?.key || String.fromCharCode(65 + idx), text: String(opt?.text || '') }));
  }
  return Object.entries(options || {}).map(([key, text]) => ({ key, text: String(text || '') }));
}

function buildTexts(question) {
  const scenario = String(question?.scenario || question?.question || '').trim();
  const options = normalizeOptions(question?.options);
  const texts = [];

  if (scenario) texts.push(scenario);
  options.forEach((opt) => texts.push(opt.text));

  return {
    scenario,
    options,
    texts,
    hasScenario: Boolean(scenario)
  };
}

export function useAssessmentTranslation() {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);
  const abortControllerRef = useRef(null);

  const translateQuestion = useCallback(async (question, targetLang, { foreground = true } = {}) => {
    if (!question || targetLang === 'en') return null;

    const cacheKey = makeCacheKey(targetLang, question);
    if (translationMemoryCache[cacheKey]) {
      return translationMemoryCache[cacheKey];
    }
    if (inFlightTranslations[cacheKey]) {
      return inFlightTranslations[cacheKey];
    }

    const parts = buildTexts(question);
    if (parts.texts.length === 0) {
      return null;
    }

    const requestPromise = (async () => {
      try {
        let signal;
        if (foreground) {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          abortControllerRef.current = new AbortController();
          signal = abortControllerRef.current.signal;
        }

        const response = await fetch(TRANSLATE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ texts: parts.texts, targetLanguage: targetLang }),
          signal
        });

        const data = await response.json();
        const translations = Array.isArray(data?.translations) ? data.translations : parts.texts;

        let cursor = 0;
        const scenarioText = parts.hasScenario ? String(translations[cursor++] || parts.scenario) : null;
        const optionTexts = parts.options.map((opt) => {
          const translated = String(translations[cursor++] || opt.text);
          return { key: opt.key, text: translated };
        });

        const result = {
          scenario: scenarioText,
          questionText: scenarioText,
          options: optionTexts
        };

        translationMemoryCache[cacheKey] = result;
        return result;
      } catch (err) {
        if (err?.name === 'AbortError') return null;
        console.warn('[Translation] Failed, showing English:', err?.message || err);
        return null;
      } finally {
        delete inFlightTranslations[cacheKey];
      }
    })();

    inFlightTranslations[cacheKey] = requestPromise;
    return requestPromise;
  }, []);

  const switchLanguage = useCallback(async (targetLang, currentQuestion, upcomingQuestions = []) => {
    setCurrentLanguage(targetLang);

    if (targetLang === 'en') {
      setTranslatedContent(null);
      return;
    }

    setIsTranslating(true);
    try {
      const translated = await translateQuestion(currentQuestion, targetLang, { foreground: true });
      setTranslatedContent(translated);

      upcomingQuestions.slice(0, 2).forEach((q) => {
        translateQuestion(q, targetLang, { foreground: false }).catch(() => {});
      });
    } finally {
      setIsTranslating(false);
    }
  }, [translateQuestion]);

  const translateCurrentQuestion = useCallback(async (question) => {
    if (!question) {
      setTranslatedContent(null);
      return;
    }

    if (currentLanguage === 'en') {
      setTranslatedContent(null);
      return;
    }

    const cacheKey = makeCacheKey(currentLanguage, question);
    if (translationMemoryCache[cacheKey]) {
      setTranslatedContent(translationMemoryCache[cacheKey]);
      return;
    }

    setIsTranslating(true);
    try {
      const translated = await translateQuestion(question, currentLanguage, { foreground: true });
      setTranslatedContent(translated);
    } finally {
      setIsTranslating(false);
    }
  }, [currentLanguage, translateQuestion]);

  const clearTranslation = useCallback(() => {
    setTranslatedContent(null);
    setCurrentLanguage('en');
  }, []);

  return {
    currentLanguage,
    isTranslating,
    translatedContent,
    switchLanguage,
    translateCurrentQuestion,
    clearTranslation
  };
}
